/* eslint-disable no-throw-literal */
import { v4 as uuidv4 } from 'uuid';
import moment from 'moment';
import __ from 'lodash';
import { invertGetCollectionDaysType } from 'utils/getPaymentType';
import store from 'app/store';
import { parseDMYFormat } from 'utils/calculateDate';
import { getDeclarationsForPolicy } from './DeclarationHelper';

// Dependent Service Risks are the one which needed a API call to verify/perform something. In return, API will return all the attributes with the values.
// Independent Service Risks are the one which does not need a API call. We just have to assign a value to all the attributes from their linked attribute.
export const getIndependentServiceRisks = (
	serviceRisks,
	values,
	risks,
	factors,
	getAttributeValueByCode,
	risksFromDraftDetails
) => {
	let result = [];
	serviceRisks?.forEach(async (serviceRisk, i) => {
		// If the serviceRisk is dependent, then don't add to the result.
		// If the serviceRisk is independent, then add to the result.
		if (!values?.serviceRisks?.find(e => e.code === serviceRisk.code)) {
			// Add only when any of the attribute is linked.
			if (serviceRisk?.attributes?.some(e => e.linkedField)) {
				// Get risk id from the resumed policy version in resume flows by risk code
				const id = risksFromDraftDetails?.find(risk => risk.code === serviceRisk.code)?.id;
				// Get riskInstanceId from the resumed policy version in resume flows by risk code
				const riskInstanceId = risksFromDraftDetails?.find(risk => risk.code === serviceRisk.code)?.instanceId;
				result = [
					...result,
					{
						productRiskReference: serviceRisk.instanceId,
						code: serviceRisk.code,
						externalReference: '',
						price: 0,
						discount: 0,
						tax: 0,
						total: 0,
						id,
						riskInstanceId,
						attributes: serviceRisk.attributes.map(attr => ({
							code: attr.code,
							name: attr.name,
							value: getAttributeValueByCode(
								null,
								attr?.linkedField?.split('~')[1]?.trim(),
								values,
								risks,
								factors
							)?.value,
							dataType: attr.dataType
						})),
						valuations: [],
						isValidServiceRisk: true
					}
				];
			}
		}
	});
	return result;
};

// optionPrice = Selected option price details from execute calculator response
export const getRisks = (values, risks, optionPrice, policiesCount = 1) => {
	let riskValues = [];
	risks.forEach(
		(risk, i) =>
			risk &&
			Object.keys(risk).forEach((field, j) => {
				const riskValuesPerCodeLength = riskValues?.filter(e => e.code === risk[field].code)?.length;

				const currentRiskOfOptionPriceRisks = optionPrice?.risks?.filter(e => e.code === risk[field].code)?.[
					riskValuesPerCodeLength
				];

				// The below condition is added because,
				// When we have duplicate or the same risk multiple times, let's say child risks,
				// We can't bind the risks with Risk Code becuase all those multiple risks carries same code.
				// Hence selectedOption.risks.ratings we have to bind 1st to 1st & 2nd to 2nd with create policy payload.
				const riskRating = currentRiskOfOptionPriceRisks?.ratings?.find(
					x => x.frequency?.toLowerCase() === values.collsFrequency?.toLowerCase()
				);

				riskValues.push({
					productRiskReference: risk[field].instanceId,
					code: risk[field].code,
					name: risk[field].name,
					externalReference: '',
					id: risk[field]?.id,
					riskInstanceId: risk[field]?.riskInstanceId,
					price: (() => {
						let priceValue;
						if (riskRating?.ratingOutfit?.apiPrice) {
							priceValue = parseFloat(riskRating.ratingOutfit.apiPrice);
						} else {
							priceValue = riskRating?.price ?? 0;
						}
						return priceValue;
					})(),
					discount: (() => {
						let discountValue;
						if (riskRating?.ratingOutfit?.apiDiscount) {
							discountValue = parseFloat(riskRating?.ratingOutfit?.apiDiscount);
						} else {
							discountValue = riskRating?.discount ?? 0;
						}
						return discountValue;
					})(),
					tax: (() => {
						let taxValue;
						if (riskRating?.ratingOutfit?.apiTax) {
							taxValue = parseFloat(riskRating?.ratingOutfit?.apiTax);
						} else {
							taxValue = riskRating?.tax ?? 0;
						}
						return taxValue;
					})(),
					total: (() => {
						let totalValue;
						if (riskRating?.ratingOutfit?.apiTotal) {
							totalValue = parseFloat(riskRating?.ratingOutfit?.apiTotal);
						} else {
							totalValue = riskRating?.total ?? 0;
						}
						return totalValue;
					})(),
					attributes: risk[field].attributes.map(attr => ({
						code: attr.code,
						name: attr.name,
						value: values[`${field}-${attr.name}`],
						dataType: attr.dataType
					})),
					valuations: currentRiskOfOptionPriceRisks?.valuations?.values ?? [],
					formType: risk[field]?.formType,
					riskType: risk[field]?.riskType
				});
			})
	);
	if (values?.serviceRisks) {
		riskValues = [...riskValues, ...(values?.serviceRisks || [])];
	} else {
		riskValues = [...riskValues];
	}

	return riskValues;
};

export const getFactors = (values, factors) => {
	const factorValues = [];
	if (factors) {
		Object.keys(factors).forEach(factor => {
			factors[factor].forEach(field => {
				factorValues.push({
					code: field.code,
					name: field.name,
					value: values[`${factor}-${field.name}`],
					dataType: field.dataType
				});
			});
		});
	}
	return factorValues;
};

// First argument for default values, and second argument for required values of policyDraft
export const getPolicyDraftDetails = (
	{ formValues, factors, risks, phoneNumberCode, phoneNumber, referenceToken, optionPrice },
	{ email, reference, instanceId, isFromCreatePolicySale = false } = {}
) => {
	const risksPayload = getRisks({ ...formValues }, risks, optionPrice ?? null);
	const factorsPayload = getFactors({ ...formValues }, factors);
	const { activeStep, productInstanceId, riskPageNo, productOptionInstanceId, collsFrequency } = formValues;

	return {
		productInstanceId,
		factors: !isFromCreatePolicySale ? factorsPayload : [],
		risks: !isFromCreatePolicySale ? risksPayload : [],
		activeStep,
		productOptionInstanceId,
		collsFrequency,
		riskPageNo,
		redirectURL: window.location,
		phoneNumberCode,
		phoneNumber,
		referenceToken,
		email,
		reference,
		instanceId
	};
};

// NOTE: This method is for assigning the values in the service risk attributes from ratingOutfit
// Currently, we doing it for PBD risk.
// risksFromDraftDetails = risks from draftPolicy.resumePolicyResponse.risks; used to bind risk id in resume flows
const getPremiumBreakdownServiceRisks = (serviceRisks, ratingOutfit, risksFromDraftDetails = []) => {
	// Finding the premium breakdown risks
	const premiumBreakdownRisk = serviceRisks?.find(risk => risk.code === 'PBD');
	if (premiumBreakdownRisk) {
		const id = risksFromDraftDetails?.find(risk => risk.code === 'PBD')?.id;
		const riskInstanceId = risksFromDraftDetails?.find(risk => risk.code === 'PBD')?.instanceId;
		const updatedAttributes = premiumBreakdownRisk?.attributes.map(attribute => {
			// Converting the attribute name into camelcase to assign the values
			const attributeName = __.camelCase(attribute.name)?.replace('proRata', 'prorata');
			if (Object?.keys(ratingOutfit || {}).some(key => key === attributeName)) {
				return { ...attribute, value: ratingOutfit[attributeName] };
			}
			return attribute;
		});
		return [
			{
				attributes: updatedAttributes
					?.filter(att => att.value)
					?.map(attr => ({
						code: attr.code,
						name: attr.name,
						value: attr.value,
						dataType: attr.dataType
					})),
				productRiskReference: premiumBreakdownRisk?.instanceId,
				code: premiumBreakdownRisk.code,
				name: premiumBreakdownRisk.name,
				externalReference: '',
				// Match by code to get id from the resumed policy version in resume flows
				id,
				// Match by code to get riskInstanceId from the resumed policy version in resume flows
				riskInstanceId,
				valuations: []
			}
		];
	}
	return [];
};

// This function is to distribute the risks based on the number of policies for each risk code.
// For example, if CHD risk has 2 policies, then we have to distribute the CHD risks into 2 policies in the request body.
// And if PHO risk has 1 policy, then we have to add the same PHO risk in both policies in the request body.
const buildPolicyRisks = (values, risks, optionPrice, _risks) => {
	// Build a list of risks with their respective policy counts
	const maxPolicies = _risks.map(risk => {
		const optionRisk = optionPrice?.risks?.find(r => r.code === risk.code);

		const num = Number(
			optionRisk?.ratings?.find(r => r?.ratingOutfit?.numberOfPolicies)?.ratingOutfit?.numberOfPolicies
		);

		return {
			name: risk.name,
			code: risk.code,
			numberOfPolicies: !num || num <= 0 ? 0 : num
		};
	});

	// Remove duplicate risks based on code, keeping one entry per risk type
	const uniquePolicies = Array.from(new Map(maxPolicies.map(i => [i.code, i])).values());

	// Find total number of policies to generate (maximum among all risks)
	const maxPoliciesCount = uniquePolicies.reduce(
		(max, item) => (item.numberOfPolicies > max ? item.numberOfPolicies : max),
		1
	);

	// Get all the risk objects (code, attributes, total, etc.)
	const allRisks = getRisks(values, risks, optionPrice, maxPoliciesCount);

	// Group risks by code for easier distribution across policies
	// Example: { CHD: [...], PHO: [...], BEN: [...] }
	const risksByCode = allRisks.reduce((acc, risk) => {
		acc[risk.code] = acc[risk.code] || [];
		acc[risk.code].push(risk);
		return acc;
	}, {});

	// Consolidate individual policies and distribute risks accordingly
	return {
		policyRisks: Array.from({ length: maxPoliciesCount }, (_, idx) => {
			const policyRisks = [];

			uniquePolicies.forEach(policy => {
				const riskList = risksByCode[policy.code] || [];

				if (policy.numberOfPolicies > 1) {
					// Multi-policy risks (e.g., CHD)
					if (riskList[idx]) {
						policyRisks.push(riskList[idx]);
					}
				} else {
					// Single-policy risks go to every policy
					if (riskList[0]) {
						policyRisks.push(riskList[0]);
					}
				}
			});

			return policyRisks;
		}),
		numberOfPolicies: maxPoliciesCount
	};
};

export const createPolicyRequestBody = ({
	values,
	risks,
	factors,
	optionPrice,
	agentDetails,
	currencyCode,
	declaration,
	serviceRisks,
	getAttributeValueByCode,
	paymentInceptionDate,
	productLifeCycles,
	selectedCollectionTypeTotalPrice,
	policyStatus,
	policyReference = null, // Optional
	doNotValidate = false, // Optional,
	doNotAddPolicyCollectionSchedule = false, // Optional
	doNotAddDeclarations = false, // Optional
	ratingOutfit // Optional
}) => {
	try {
		// Validating Product Option...
		if (!doNotValidate) {
			if (!values?.collsIsSelectedProductOptionValid) {
				throw 'Selected option is not available.';
			} else if (!values?.collsTotalPrice || values?.collsTotalPrice <= 0) {
				throw 'Invalid total price.';
			}
		}

		const channelSettings = store.getState().fuse?.form?.channelSettings?.value;

		const failedAgentToken = store.getState().fuse?.form?.failedAgentTokens;

		const searchPolicyResponse = store.getState().fuse?.form?.searchPolicyResponse?.value;

		const selectedCollectionType = parseInt(values?.collsPaymentProvider?.collectionType, 10);

		const paymentRecurringCollectionDay = parseInt(
			invertGetCollectionDaysType(values?.collsPaymentRecurringCollectionDay),
			10
		);

		const nonBusinessDayRule = values?.collsPaymentProvider?.nonBusinessDayRule;

		const paymentFirstCollectionDate = parseDMYFormat(
			values?.collsPaymentFirstCollectionDate,
			'YYYY-MM-DDTHH:mm:ss[Z]'
		);

		// Risks from the resumed policy version provide the risk id for each risk in resume flows.
		const risksFromDraftDetails = store.getState().fuse?.form?.draftPolicy?.resumePolicyResponse?.risks ?? [];

		const independentServiceRisks = getIndependentServiceRisks(
			serviceRisks,
			values,
			risks,
			factors,
			getAttributeValueByCode,
			risksFromDraftDetails
		);

		const premiumBreakDownServiceRisks = getPremiumBreakdownServiceRisks(
			serviceRisks,
			ratingOutfit,
			risksFromDraftDetails
		);

		const { attributes: serviceSummaryRiskAttributes } =
			(independentServiceRisks &&
				independentServiceRisks?.length > 0 &&
				independentServiceRisks?.find(e => e.code === 'SERVICESUM')) ||
			[];

		const TERMS = {
			0: 'none',
			1: 'hours',
			2: 'days',
			5: 'months',
			6: 'years'
		};

		const startDate =
			(serviceSummaryRiskAttributes && serviceSummaryRiskAttributes[0]?.value) ||
			(ratingOutfit?.policyStartDate &&
				moment(ratingOutfit?.policyStartDate, ['DD/MM/YYYY']).format('YYYY-MM-DDTHH:mm:ss[Z]')) ||
			(paymentInceptionDate && moment(paymentInceptionDate, ['DD-MM-YYYY']).format('YYYY-MM-DDTHH:mm:ss[Z]')) ||
			moment(new Date(), 'DD-MM-YYYY').format('YYYY-MM-DDTHH:mm:ss[Z]');

		const endDate =
			(serviceSummaryRiskAttributes && serviceSummaryRiskAttributes[1]?.value) ||
			(ratingOutfit?.policyEndDate &&
				moment(ratingOutfit?.policyEndDate, ['DD/MM/YYYY']).format('YYYY-MM-DDTHH:mm:ss[Z]')) ||
			moment(paymentInceptionDate ?? new Date(), ['DD-MM-YYYY'])
				.add(productLifeCycles?.duration ?? 1, TERMS[productLifeCycles?.termDuration ?? 6])
				.subtract(1, 'day')
				.add({ hours: 23, minutes: 59, seconds: 59 })
				.format('YYYY-MM-DDTHH:mm:ss[Z]');

		// Get the resume policy OTP if exists in resume flow
		const getReferralPolicyOtp = store.getState().fuse?.form?.draftPolicy?.resumePolicyOtp
			? [
					{
						value: store.getState().fuse?.form?.draftPolicy?.resumePolicyOtp,
						category: 6 // Category 6 is Referral
					}
				]
			: [];

		// Get agent selling token from reference token details
		const referenceTokenDetails = store.getState().fuse?.form?.referenceTokenDetails;

		// Get all the risk objects (code, attributes, total, etc.)
		const allRisks = getRisks(values, risks, optionPrice, 0);

		// Get all the risk objects (code, attributes, total, etc.) & number of policies based on ratings
		const { policyRisks, numberOfPolicies = 1 } = buildPolicyRisks(values, risks, optionPrice, allRisks) || {};

		return {
			externalReference: uuidv4(),
			networkId: '',
			channelId: `${channelSettings?.channel?.id}`,
			companyId: agentDetails?.partnerCode || '',
			brokerId: '',
			agentId: agentDetails?.agentInstanceId || '',
			agentSellingCode: referenceTokenDetails?.token || '',
			comment: '',
			productId: values.productInstanceId,
			policies: Array.from({ length: numberOfPolicies }, (_, idx) => ({
				externalReference: uuidv4(),
				policyReference,
				existingPolicyReference:
					ratingOutfit?.isLinked === 'True' ? searchPolicyResponse?.policyReference ?? '' : '',
				productOptionReference: values?.productOptionInstanceId,
				startDate,
				endDate,
				status: policyStatus,
				currencyCode,
				otpReference: values[Object.keys(values)?.find(key => key.includes('otpDec'))]?.split('~')[1] ?? '',
				comment: failedAgentToken?.token ? `Failed token - ${failedAgentToken?.token}` : '',
				originAttributes: JSON.stringify({
					redirectURL: window.location,
					collections: { collsFrequency: values?.collsFrequency }
				}),
				risks: [
					...(numberOfPolicies > 1 ? policyRisks[idx] : allRisks),
					...independentServiceRisks,
					...premiumBreakDownServiceRisks
				],
				attributes: getFactors(values, factors),
				declarations: !doNotAddDeclarations ? [...getDeclarationsForPolicy(declaration, values)] : [],
				policyCollectionSchedule: !doNotAddPolicyCollectionSchedule
					? {
							collectionDay: paymentRecurringCollectionDay || 1, // Recurring Payment Day
							collectionDuration: productLifeCycles?.termDuration, // Life Cycle - Term & Periods - Term Duration
							collectionFrequency: selectedCollectionType,
							collectionNonBusinessDayRule: nonBusinessDayRule,
							policyFirstCollection: {
								firstCollectionAmount: parseFloat(selectedCollectionTypeTotalPrice),
								firstCollectionDate: paymentFirstCollectionDate, // First Payment Date
								isTaxInclusive: true,
								firstCollectionTaxAmount: 0,
								comments: ''
							}
						}
					: null,
				otps: [...getReferralPolicyOtp] // Passing the captured OTPs from resume flow.
			}))
		};
	} catch (ex) {
		throw ex?.message || ex;
	}
};

// Function to create the request body for createLead API
export const createLeadRequestBody = ({
	values,
	risks,
	factors,
	declaration,
	productOptionReference,
	comment = '',
	doNotAddAgentId = false // Optional
}) => {
	const channelSettings = store.getState().fuse?.form?.channelSettings?.value;
	const agentDetails = store.getState().fuse?.form?.referenceTokenDetails?.agentDetails;

	const leadRisks = getRisks(values, risks, null);
	const leadAttributes = getFactors(values, factors);
	const leadDeclarations = getDeclarationsForPolicy(declaration, values);

	return {
		externalReference: uuidv4(),
		networkId: '',
		channelId: `${channelSettings?.channel?.id ?? ''}`,
		companyId: agentDetails?.partnerCode || '',
		productId: values?.productInstanceId || '',
		brokerId: '',
		agentId: !doNotAddAgentId ? agentDetails?.agentInstanceId || '' : '',
		comment: '',
		leads: [
			{
				externalReference: uuidv4(),
				productOptionReference: productOptionReference || values?.productOptionInstanceId || null,
				status: 1,
				otpReference: values[Object.keys(values)?.find(key => key.includes('otpDec'))]?.split('~')[1] ?? '',
				comment: comment || '',
				originAttributes: JSON.stringify({
					redirectURL: window.location,
				}),
				risks: leadRisks?.map(r => ({
					productRiskReference: r.productRiskReference,
					externalReference: r.externalReference || '',
					name: r.name,
					code: r.code,
					attributes:
						r.attributes?.map(a => ({
							name: a.name,
							value: a.value,
							code: a.code,
							dataType: a.dataType
						})) || []
				})),
				attributes:
					leadAttributes?.map(a => ({
						name: a.name,
						value: a.value,
						code: a.code,
						dataType: a.dataType
					})) || [],
				declarations:
					leadDeclarations?.map(d => ({
						declarationAcceptType: d.declarationAcceptType,
						declarationId: d.declarationId,
						name: d.name,
						value: d.value
					})) || []
			}
		]
	};
};
